import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sectionIdFromFundType } from '../types/server-data/ClientConfig';
import { updateActiveDate } from '../redux/pages/actions';

interface NavigateItem {
  date?: string;
  id?: string;
}

export function useComplianceNavigator() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  function navigateWithDatePath(basePath: string, item?: NavigateItem) {
    let url = basePath;

    if (item?.date && item?.id) {
      dispatch(updateActiveDate(item.date));
      const searchParams = new URLSearchParams({
        fundId: item.id,
        positionDate: item.date,
      });
      url += `?${searchParams.toString()}`;
    }

    navigate(url);
  }

  function getComplianceViewPath(
    regulation: string,
    subcategory: string,
  ): string {
    const section =
      sectionIdFromFundType(regulation) ?? regulation.toLowerCase();
    const key = subcategory || regulation;

    if (section.toUpperCase() === key) {
      return `/${section}/${section}-law`;
    }

    // pe-exposure, pe-overview and pe-value-at-risk are being deprecated and
    // standardised onto the shared pages. PE is not live for any client yet.
    if (section === 'pe' && key === 'Commitment') {
      return `/${section}/pe-exposure`;
    }

    // AIFMD sections have no value-at-risk page. bflexion, quayside, wcapital
    // and zebedee are still on the legacy MANCO_DASHBOARD, not this one.
    if (section === 'aifmd' && key === 'Value at Risk') {
      return `/${section}/exposure`;
    }

    const fallbackMap: Record<string, string> = {
      Commitment: `/${section}/exposure`,
      'Value at Risk': `/${section}/value-at-risk`,
      Prospectus: `/${section}/prospectus-restrictions`,
      'CSSF Risk Spreading': `/${section}/cssf-risk-spreading`,
      Leverage: `/${section}/exposure`,
      'Ucits Law': `/${section}/ucits-law`,
      'Supplementary Rules': `/${section}/supplementary-rules`,
    };

    const basePath = fallbackMap[key];
    if (basePath) {
      return basePath;
    }

    // Default fallback
    return `/${section}/exposure`;
  }

  function navigateToComplianceView(
    regulation: string,
    subcategory: string,
    item?: NavigateItem,
  ) {
    const basePath = getComplianceViewPath(regulation, subcategory);
    navigateWithDatePath(basePath, item);
  }

  return {
    navigateToComplianceView,
    getComplianceViewPath,
  };
}
